import { SocialLink } from './brand.model';

/** A promotional banner (feeds the Offers rail). */
export interface Banner {
  title: string;
  subtitle: string | null;
  imageUrl: string | null;
  mobileImageUrl: string | null;
  actionType: string;
  actionValue: string;
}

/** A curated collection (best_sellers, hot_items…) — items fetched from itemsUrl. */
export interface HomeCollection {
  id: string;
  type: string;
  name: string;
  displayName: string;
  itemsUrl: string;
}

/** A menu item as returned by a collection's items endpoint. */
export interface HomeItem {
  id: string;
  name: string;
  description: string | null;
  price: number;
  image: string | null;
}

/** An online-enabled branch from the brand config (no name/address/status in the API). */
export interface StoreBranchOrderType {
  type: string; // delivery | pickup | dine_in
  estimatedMinutes: number | null;
  minOrderAmount: number | null;
  freeDeliveryThreshold: number | null;
  schedulingEnabled: boolean;
}
export interface StoreBranch {
  id: string;
  branchId: string;
  orderTypes: StoreBranchOrderType[];
}

/** Published storefront content (hero, sections, story…) from the admin config. */
export interface HeroConfig { badge: string; title1: string; title2: string; subtitle: string; cta: string; image: string | null; video: string | null; }
/** "Our story": copy plus a 16:9 image and optional video, like the hero. */
export interface StoryConfig { kicker: string; title: string; body: string; image: string | null; video: string | null; }
export interface LoyaltyConfig { title: string; body: string; }
export interface HomeSection { id: string; label: string; on: boolean; }
/** Legal copy entered by the operator (rendered verbatim, admin's own formatting). */
export interface LegalConfig { terms: string; privacy: string; }
export interface StorefrontConfig {
  hotlineHours: string;
  /** The admin's footer line, shown on every page (localized by the API). */
  footerText: string;
  hero: HeroConfig;
  story: StoryConfig;
  loyalty: LoyaltyConfig;
  social: SocialLink[];
  legal: LegalConfig;
  homepage: { enabled: boolean; sections: HomeSection[] };
}

/**
 * Structural defaults only — NO tenant/sample copy. Every user-visible string
 * (hero, story, loyalty, legal, social, hotline hours) comes from the API
 * (/api/v1/brand). These blanks mean nothing renders until the config loads;
 * only the homepage section catalogue (ids + generic labels) is kept so the
 * page can lay out sections the API enables.
 */
export const STOREFRONT: StorefrontConfig = {
  hotlineHours: '',
  footerText: '',
  hero: { badge: '', title1: '', title2: '', subtitle: '', cta: 'Start your order', image: null, video: null },
  story: { kicker: '', title: '', body: '', image: null, video: null },
  loyalty: { title: '', body: '' },
  legal: { terms: '', privacy: '' },
  social: [],
  homepage: {
    enabled: true,
    sections: [
      { id: 'offers', label: 'Offers & promotions', on: true },
      { id: 'bestsellers', label: 'Best sellers', on: true },
      { id: 'categories', label: 'Explore categories', on: true },
      { id: 'popular', label: 'Popular items', on: true },
      { id: 'story', label: 'Brand story', on: true },
      { id: 'branches', label: 'Branch locator', on: true },
      { id: 'reviews', label: 'Customer reviews', on: true },
      { id: 'loyalty', label: 'Loyalty teaser', on: true },
      { id: 'social', label: 'Social links', on: true },
      { id: 'hotline', label: 'Call-to-order hotline', on: true },
    ],
  },
};
